import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axiosAuth from "../axiosConfig";
import { toast } from "react-toastify";

const SectionCard = ({ title, children, right }) => (
  <section className="bg-white dark:bg-neutral-900 rounded-2xl shadow-lg border-l-4 border-blue-600 dark:border-blue-500 p-5 sm:p-7 transition-colors">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-semibold text-blue-800 dark:text-blue-300">
        {title}
      </h2>
      {right}
    </div>
    {children}
  </section>
);

const Field = ({
  label,
  value,
  onChange,
  placeholder,
  readOnly,
  type = "text",
}) => (
  <div>
    <label className="block text-gray-700 dark:text-gray-200 font-medium mb-1 text-sm">
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-500 transition-colors ${
        readOnly
          ? "bg-gray-100 dark:bg-neutral-800 cursor-not-allowed border-gray-300 dark:border-neutral-700 text-gray-700 dark:text-gray-300"
          : "bg-white dark:bg-neutral-900 border-gray-300 dark:border-neutral-700 text-gray-900 dark:text-gray-100"
      }`}
    />
  </div>
);

export default function MyProfile() {
  const [kyc, setKyc] = useState(null);
  const [overallStatus, setOverallStatus] = useState("unverified"); // NEW
  const [isKycVerifiedFlag, setIsKycVerifiedFlag] = useState(false); // NEW

  const [aadhaar, setAadhaar] = useState("");
  const [aadhaarOtp, setAadhaarOtp] = useState("");
  const [aadhaarReqId, setAadhaarReqId] = useState("");
  const [pan, setPan] = useState("");
  const [banks, setBanks] = useState([]);
  const [upis, setUpis] = useState([]);
  const [bankForm, setBankForm] = useState({
    bankName: "",
    accountHolder: "",
    accountNumber: "",
    ifsc: "",
    branch: "",
  });
  const [upiForm, setUpiForm] = useState({ upiId: "" });

  const [loading, setLoading] = useState(true);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [verifyingPan, setVerifyingPan] = useState(false);
  const [addingBank, setAddingBank] = useState(false);
  const [addingUpi, setAddingUpi] = useState(false);

  const api = (ep) => {
    const base =
      axiosAuth.defaults.baseURL || "http://localhost:5000/api/auth";
    return `${base.replace("/api/auth", "")}${ep}`;
  };

  useEffect(() => {
    fetchKyc();
    fetchBanks();
    fetchUpis();
  }, []);

  async function fetchKyc() {
    try {
      setLoading(true);
      const res = await axiosAuth.get(api("/api/kyc/status"));
      if (res.data?.success) {
        const { kyc: kycPayload, overallStatus, isKycVerified } = res.data;

        setKyc(kycPayload || {});
        setOverallStatus(
          overallStatus ||
            kycPayload?.status ||
            (isKycVerified ? "verified" : "unverified")
        );
        setIsKycVerifiedFlag(!!isKycVerified);
      } else {
        toast.error("Failed to load KYC info");
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load KYC status");
    } finally {
      setLoading(false);
    }
  }

  async function fetchBanks() {
    try {
      const res = await axiosAuth.get(api("/api/bank/list"));
      setBanks(res.data?.banks || []);
    } catch (err) {
      console.error(err);
    }
  }

  async function fetchUpis() {
    try {
      const res = await axiosAuth.get(api("/api/upi/list"));
      setUpis(res.data?.upis || []);
    } catch (err) {
      console.error(err);
    }
  }

  // Aadhaar OTP flow
  async function sendAadhaarOtp() {
    if (!/^\d{12}$/.test(aadhaar))
      return toast.error("Enter valid 12-digit Aadhaar");
    setSendingOtp(true);
    try {
      const res = await axiosAuth.post(api("/api/kyc/aadhaar/generate-otp"), {
        aadhaarNumber: aadhaar,
      });
      if (res.data.success) {
        setAadhaarReqId(res.data.requestId);
        toast.success("OTP sent to Aadhaar-linked mobile");
      } else toast.error(res.data.message);
    } catch (err) {
      toast.error("Failed to send Aadhaar OTP");
    } finally {
      setSendingOtp(false);
    }
  }

  async function verifyAadhaarOtp() {
    if (!aadhaarOtp || !aadhaarReqId)
      return toast.error("Enter OTP first");
    setVerifyingOtp(true);
    try {
      const res = await axiosAuth.post(api("/api/kyc/aadhaar/submit-otp"), {
        requestId: aadhaarReqId,
        otp: aadhaarOtp,
      });
      if (res.data.success) {
        toast.success("Aadhaar verified!");
        await fetchKyc();
      } else toast.error(res.data.message);
    } catch {
      toast.error("Aadhaar verification failed");
    } finally {
      setVerifyingOtp(false);
    }
  }

  async function verifyPan() {
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan.toUpperCase()))
      return toast.error("Enter valid PAN");
    setVerifyingPan(true);
    try {
      const res = await axiosAuth.post(api("/api/kyc/pan/verify"), {
        panNumber: pan.toUpperCase(),
      });
      if (res.data.success) {
        toast.success("PAN verified!");
        await fetchKyc();
      } else toast.error(res.data.message);
    } catch {
      toast.error("PAN verification failed");
    } finally {
      setVerifyingPan(false);
    }
  }

  // Add new bank
  async function addBank() {
    if (!bankForm.accountNumber || !bankForm.ifsc)
      return toast.error("Fill all bank fields");
    setAddingBank(true);
    try {
      const res = await axiosAuth.post(api("/api/bank/add"), bankForm);
      if (res.data.success) toast.success("Bank added successfully");
      await fetchBanks();
      setBankForm({
        bankName: "",
        accountHolder: "",
        accountNumber: "",
        ifsc: "",
        branch: "",
      });
    } catch {
      toast.error("Bank add failed");
    } finally {
      setAddingBank(false);
    }
  }

  // Add UPI
  async function addUpi() {
    if (!upiForm.upiId.includes("@"))
      return toast.error("Enter valid UPI ID");
    setAddingUpi(true);
    try {
      const res = await axiosAuth.post(api("/api/upi/add"), upiForm);
      if (res.data.success) toast.success("UPI added successfully");
      await fetchUpis();
      setUpiForm({ upiId: "" });
    } catch {
      toast.error("UPI add failed");
    } finally {
      setAddingUpi(false);
    }
  }

  // Set default bank
  async function setDefaultBank(id) {
    try {
      const res = await axiosAuth.patch(api(`/api/bank/default/${id}`));
      if (res.data.success) {
        toast.success("Default bank updated");
        await fetchBanks();
      } else toast.error("Failed to update default bank");
    } catch {
      toast.error("Error updating default bank");
    }
  }

  // Set default UPI
  async function setDefaultUpi(id) {
    try {
      const res = await axiosAuth.patch(api(`/api/upi/default/${id}`));
      if (res.data.success) {
        toast.success("Default UPI updated");
        await fetchUpis();
      } else toast.error("Failed to update default UPI");
    } catch {
      toast.error("Error updating default UPI");
    }
  }

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-blue-700 dark:text-blue-300">
        Loading KYC status…
      </div>
    );

  // helper for top badge
  const renderKycBadge = () => {
    if (overallStatus === "verified") {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
          ✅ KYC Verified
        </span>
      );
    }
    if (overallStatus === "rejected") {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
          ❌ KYC Rejected
        </span>
      );
    }
    if (overallStatus === "partially_verified") {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300">
          ⏳ Partially Verified
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 dark:bg-neutral-800 dark:text-gray-300">
        ⏳ Not Verified
      </span>
    );
  };

  return (
    <motion.div
      className="min-h-screen bg-blue-50 dark:bg-neutral-950 p-5 sm:p-8 space-y-6 text-sm sm:text-base transition-colors"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold text-blue-800 dark:text-blue-300">
          My Profile
        </h1>
        <div className="flex flex-col sm:items-end gap-1">
          <div>{renderKycBadge()}</div>
          {overallStatus === "rejected" && kyc?.rejectedReason && (
            <p className="text-xs text-red-600 dark:text-red-300">
              Reason: {kyc.rejectedReason}
            </p>
          )}
        </div>
      </div>

      {/* Aadhaar */}
      <SectionCard
        title="Aadhaar e-KYC"
        right={
          kyc?.aadhaar?.verified ? (
            <span className="text-green-600">
              ✅ Document Verified
            </span>
          ) : (
            <span className="text-yellow-600">⏳ Pending</span>
          )
        }
      >
        {kyc?.aadhaar?.verified ? (
          <div className="text-green-700 dark:text-green-300 space-y-1">
            <p>
              <strong>Name:</strong> {kyc.aadhaar.name}
            </p>
            <p>
              <strong>DOB:</strong> {kyc.aadhaar.dob}
            </p>
            <p>
              <strong>Gender:</strong> {kyc.aadhaar.gender}
            </p>
            <p>
              <strong>Aadhaar No:</strong> {kyc.aadhaar.number}
            </p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-3">
              <Field
                label="Aadhaar Number"
                value={aadhaar}
                onChange={(e) => setAadhaar(e.target.value)}
                placeholder="12-digit Aadhaar"
              />
              <button
                onClick={sendAadhaarOtp}
                disabled={sendingOtp}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 h-fit mt-5"
              >
                {sendingOtp ? "Sending…" : "Send OTP"}
              </button>
            </div>
            {aadhaarReqId && (
              <div className="grid md:grid-cols-2 gap-3 mt-4">
                <Field
                  label="Enter OTP"
                  value={aadhaarOtp}
                  onChange={(e) => setAadhaarOtp(e.target.value)}
                  placeholder="6-digit OTP"
                />
                <button
                  onClick={verifyAadhaarOtp}
                  disabled={verifyingOtp}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 h-fit mt-5"
                >
                  {verifyingOtp ? "Verifying…" : "Verify Aadhaar"}
                </button>
              </div>
            )}
          </>
        )}
      </SectionCard>

      {/* PAN */}
      <SectionCard
        title="PAN Verification"
        right={
          kyc?.pan?.verified ? (
            <span className="text-green-600">
              ✅ Document Verified
            </span>
          ) : (
            <span className="text-yellow-600">⏳ Pending</span>
          )
        }
      >
        {kyc?.pan?.verified ? (
          <div className="text-green-700 dark:text-green-300 space-y-1">
            <p>
              <strong>Name:</strong> {kyc.pan.name}
            </p>
            <p>
              <strong>PAN:</strong> {kyc.pan.number}
            </p>
            <p>
              <strong>Category:</strong> {kyc.pan.category}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            <Field
              label="PAN Number"
              value={pan}
              onChange={(e) => setPan(e.target.value.toUpperCase())}
              placeholder="ABCDE1234F"
            />
            <button
              onClick={verifyPan}
              disabled={verifyingPan}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 h-fit mt-5"
            >
              {verifyingPan ? "Verifying…" : "Verify PAN"}
            </button>
          </div>
        )}
      </SectionCard>

      {/* Banks */}
      <SectionCard
        title="Bank Accounts"
        right={
          <span className="text-blue-600 font-medium">
            {banks.length} saved
          </span>
        }
      >
        <div className="space-y-3">
          {banks.map((b, i) => (
            <div
              key={i}
              className={`p-3 border rounded-lg flex justify-between items-center ${
                b.isDefault
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/10"
                  : "bg-gray-50 dark:bg-neutral-800"
              }`}
            >
              <div>
                <p className="font-medium text-blue-700 dark:text-blue-300">
                  {b.bankName}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {b.accountNumber} • {b.ifsc}
                </p>
                <p className="text-xs">
                  {b.status === "verified"
                    ? "✅ Verified"
                    : "⏳ Pending"}
                </p>
              </div>
              <div>
                {b.isDefault ? (
                  <span className="text-xs text-green-600 font-medium">
                    ⭐ Default
                  </span>
                ) : (
                  <button
                    onClick={() => setDefaultBank(b._id)}
                    className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                  >
                    Set Default
                  </button>
                )}
              </div>
            </div>
          ))}

          <div className="grid md:grid-cols-2 gap-3 mt-3">
            <Field
              label="Bank Name"
              value={bankForm.bankName}
              onChange={(e) =>
                setBankForm({ ...bankForm, bankName: e.target.value })
              }
              placeholder="e.g. HDFC Bank"
            />
            <Field
              label="Account Holder"
              value={bankForm.accountHolder}
              onChange={(e) =>
                setBankForm({
                  ...bankForm,
                  accountHolder: e.target.value,
                })
              }
              placeholder="As per bank"
            />
            <Field
              label="Account Number"
              value={bankForm.accountNumber}
              onChange={(e) =>
                setBankForm({
                  ...bankForm,
                  accountNumber: e.target.value,
                })
              }
              placeholder="Digits only"
            />
            <Field
              label="IFSC"
              value={bankForm.ifsc}
              onChange={(e) =>
                setBankForm({
                  ...bankForm,
                  ifsc: e.target.value.toUpperCase(),
                })
              }
              placeholder="HDFC0001234"
            />
            <Field
              label="Branch"
              value={bankForm.branch}
              onChange={(e) =>
                setBankForm({ ...bankForm, branch: e.target.value })
              }
              placeholder="Branch name"
            />
          </div>
          <button
            onClick={addBank}
            disabled={addingBank}
            className="mt-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2"
          >
            {addingBank ? "Adding…" : "Add Bank"}
          </button>
        </div>
      </SectionCard>

      {/* UPI IDs */}
      <SectionCard
        title="UPI IDs"
        right={
          <span className="text-blue-600 font-medium">
            {upis.length} saved
          </span>
        }
      >
        <div className="space-y-3">
          {upis.map((u, i) => (
            <div
              key={i}
              className={`p-3 border rounded-lg flex justify-between items-center ${
                u.isDefault
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/10"
                  : "bg-gray-50 dark:bg-neutral-800"
              }`}
            >
              <div>
                <p className="font-medium text-blue-700 dark:text-blue-300">
                  {u.upiId}
                </p>
                <p className="text-xs">
                  {u.status === "verified"
                    ? "✅ Verified"
                    : "⏳ Pending"}
                </p>
              </div>
              <div>
                {u.isDefault ? (
                  <span className="text-xs text-green-600 font-medium">
                    ⭐ Default
                  </span>
                ) : (
                  <button
                    onClick={() => setDefaultUpi(u._id)}
                    className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                  >
                    Set Default
                  </button>
                )}
              </div>
            </div>
          ))}

          <div className="grid md:grid-cols-2 gap-3 mt-3">
            <Field
              label="UPI ID"
              value={upiForm.upiId}
              onChange={(e) =>
                setUpiForm({ upiId: e.target.value })
              }
              placeholder="username@bank"
            />
          </div>
          <button
            onClick={addUpi}
            disabled={addingUpi}
            className="mt-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2"
          >
            {addingUpi ? "Adding…" : "Add UPI"}
          </button>
        </div>
      </SectionCard>
    </motion.div>
  );
}
